import { PageController } from '@client/components/_core/layout/PageController';
import { EntityListBody } from '@client/components/_shared/tables/EntityListBody';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CONTROL_LIST_STALE_ERROR, controlListColumns } from '@client/components/controls/control-columns';
import { useControlFilterConfig } from '@client/components/controls/useControlFilterConfig';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { TableViewsBar, useEntityBuiltInViews, useTableViews } from '@client/components/table-views';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { DEEP_LIST_OFFSET_LIMIT, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import { z } from 'zod';

const ControlSearchSchema = z.object({
    'control-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner'),
                    operator: FilterOperatorSchema,
                    value: z.coerce.bigint(),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.literal('handling-party-ids'),
                    operator: FilterOperatorSchema,
                    value: z.array(z.coerce.bigint()),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.literal('type'),
                    operator: FilterOperatorSchema,
                    value: z.string(),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
    view: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/controls/register/')({
    staticData: {
        ui: {
            title: 'Control register',
            subtitle: 'Manage and track your risk controls',
        },
    },
    validateSearch: zodValidator(ControlSearchSchema),
    component: ControlsRegisterSearch,
});

type QueryParams = RouterInputs['control']['listControls'];

const CONTROL_REGISTER_COLUMNS_PERSIST_KEY = 'control-register-table-columns';

function ControlsRegisterSearch() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();
    const { config: filterConfig, ready: filterConfigReady } = useControlFilterConfig();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        where: buildInitialWhereFromFilters(filterConfig, searchParams['control-filters']),
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isPending, error, refetch, isFetching } = trpc.control.listControls.useQuery(input, {
        enabled: filterConfigReady && viewsReady,
        meta: { errorSurface: 'inline' },
    });

    const columns = controlListColumns(orgId, 'register');

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            maxAccessibleRecords: DEEP_LIST_OFFSET_LIMIT,
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: CONTROL_REGISTER_COLUMNS_PERSIST_KEY,
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        emptyState: { noRecordsTitle: 'No controls yet' },
    });

    const builtInViews = useEntityBuiltInViews('owner');
    const views = useTableViews({
        entityType: 'CONTROL',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'control-filters',
        builtInViews,
        initialFilters: searchParams['control-filters'],
        initialActiveViewId: searchParams.view,
        queryParams: input,
        setQueryParams: setInput,
        table: tableProps,
        columnPersistKey: CONTROL_REGISTER_COLUMNS_PERSIST_KEY,
        defaultOrderBy: { id: 'desc' },
        defaultPageSize: DEFAULT_PAGE_SIZE,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />
            <EntityListBody
                table={tableProps}
                error={error}
                blockingError={hasBlockingQueryError(error, data?.rows)}
                onRetry={() => void refetch()}
                retrying={isFetching}
                staleError={CONTROL_LIST_STALE_ERROR}
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                toolbarExtra={
                    <Button.Add.Link
                        label="Add control"
                        to="/console/$orgId/controls/register/add"
                        params={{ orgId }}
                        variant="primary"
                    />
                }
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick: (e) =>
                        navigate({ to: '/console/$orgId/controls/register/details/$id', params: { orgId, id } }, e),
                })}
            />
        </PageController>
    );
}
